export type AppPublicPathEnvReader = (key: string) => string | null | undefined;

export type AppPublicPathHelpers = {
  browserBaseName: string;
  isAppLocalPathname: (browserPathname: string, appLocalPathname: string) => boolean;
  normalizeAppLocalUrlPath: (appLocalUrlPath: string) => string;
  toAppLocalPathname: (browserPathname: string) => string;
  toAppLocalUrlPath: (browserUrlPath: string) => string;
  toBrowserPath: (appLocalUrlPath: string) => string;
};

export type AppPublicPathBuildInput = {
  mode: string | undefined;
  publicPath: string | undefined;
};

type ProcessEnvLike = {
  env?: Record<string, string | undefined>;
};

function readProcessEnv(key: string) {
  const processLike = (globalThis as { process?: ProcessEnvLike }).process;
  if (processLike?.env && Object.prototype.hasOwnProperty.call(processLike.env, key)) {
    return processLike.env[key];
  }
  return undefined;
}

function rejectInvalidAppPublicPath(rawValue: string): never {
  throw new Error(
    `App public path must be "/" or an absolute path mount without query, fragment, dot segments, or origin; received ${JSON.stringify(rawValue)}.`,
  );
}

export function normalizeAppPublicPath(rawValue: string | null | undefined) {
  const trimmed = String(rawValue ?? "").trim();
  if (!trimmed || trimmed === "/") {
    return "";
  }
  if (
    !trimmed.startsWith("/")
    || trimmed.startsWith("//")
    || trimmed.includes("?")
    || trimmed.includes("#")
    || trimmed.includes("\\")
    || /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmed)
  ) {
    rejectInvalidAppPublicPath(trimmed);
  }
  const normalized = trimmed.replace(/\/+$/, "");
  if (!normalized || normalized === "/") {
    return "";
  }
  const segments = normalized.slice(1).split("/");
  if (segments.some((segment) => !segment || segment === "." || segment === "..")) {
    rejectInvalidAppPublicPath(trimmed);
  }
  return normalized;
}

export function resolveAppPublicPathForBuild(input: AppPublicPathBuildInput) {
  const normalizedPublicPath = input.publicPath === undefined
    ? undefined
    : normalizeAppPublicPath(input.publicPath);
  if (input.mode === "production" && normalizedPublicPath === undefined) {
    throw new Error("A registry-derived PUBLIC_PATH is required for production builds.");
  }

  return normalizedPublicPath ? `${normalizedPublicPath}/` : "/";
}

export function readAppPublicPathFromEnv(readEnv: AppPublicPathEnvReader = readProcessEnv) {
  const publicPath = readEnv("PUBLIC_PATH");
  if (typeof publicPath === "string") {
    return normalizeAppPublicPath(publicPath);
  }
  return "";
}

function normalizeAppLocalPath(rawPath: string) {
  const trimmed = String(rawPath ?? "").trim();
  if (!trimmed) {
    throw new Error("App-local path must not be empty.");
  }
  if (
    !trimmed.startsWith("/")
    || trimmed.startsWith("//")
    || trimmed.includes("\\")
    || /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmed)
  ) {
    throw new Error(`App-local path must be a same-origin absolute path; received ${JSON.stringify(rawPath)}.`);
  }
  return trimmed;
}

export function normalizeAppLocalUrlPath(appLocalUrlPath: string, appPublicPath?: string | null) {
  const normalizedPath = normalizeAppLocalPath(appLocalUrlPath);
  const normalizedPublicPath = appPublicPath === undefined
    ? readAppPublicPathFromEnv()
    : normalizeAppPublicPath(appPublicPath);
  const { pathname } = splitUrlPathSuffix(normalizedPath);
  if (
    normalizedPublicPath
    && (pathname === normalizedPublicPath || pathname.startsWith(`${normalizedPublicPath}/`))
  ) {
    throw new Error(
      `App-local path ${JSON.stringify(normalizedPath)} must not include app public path ${JSON.stringify(normalizedPublicPath)}.`,
    );
  }
  return normalizedPath;
}

function splitUrlPathSuffix(normalizedPath: string) {
  const suffixIndex = normalizedPath.search(/[?#]/);
  if (suffixIndex === -1) {
    return {
      pathname: normalizedPath,
      suffix: "",
    };
  }
  return {
    pathname: normalizedPath.slice(0, suffixIndex) || "/",
    suffix: normalizedPath.slice(suffixIndex),
  };
}

function stripAppPublicPath(normalizedPath: string, normalizedPublicPath: string) {
  if (!normalizedPublicPath) {
    return normalizedPath;
  }
  const { pathname, suffix } = splitUrlPathSuffix(normalizedPath);
  if (pathname === normalizedPublicPath) {
    return `/${suffix}`;
  }
  if (!pathname.startsWith(`${normalizedPublicPath}/`)) {
    throw new Error(
      `Browser path ${JSON.stringify(normalizedPath)} must start with app public path ${JSON.stringify(normalizedPublicPath)}.`,
    );
  }
  return `${pathname.slice(normalizedPublicPath.length) || "/"}${suffix}`;
}

export function withAppPublicUrlPath(appLocalPath: string, appPublicPath?: string | null) {
  const normalizedPublicPath = appPublicPath === undefined
    ? readAppPublicPathFromEnv()
    : normalizeAppPublicPath(appPublicPath);
  const normalizedLocalPath = normalizeAppLocalUrlPath(appLocalPath, normalizedPublicPath);
  if (!normalizedPublicPath) {
    return normalizedLocalPath;
  }
  if (normalizedLocalPath === "/") {
    return `${normalizedPublicPath}/`;
  }
  return `${normalizedPublicPath}${normalizedLocalPath}`;
}

export function toAppLocalUrlPath(browserPath: string, appPublicPath?: string | null) {
  const normalizedPath = normalizeAppLocalPath(browserPath);
  const normalizedPublicPath = appPublicPath === undefined
    ? readAppPublicPathFromEnv()
    : normalizeAppPublicPath(appPublicPath);
  return stripAppPublicPath(normalizedPath, normalizedPublicPath);
}

export function matchesAppLocalUrlPath(
  routePath: string,
  appLocalPath: string,
  appPublicPath?: string | null,
) {
  const resolvedRoute = splitUrlPathSuffix(toAppLocalUrlPath(routePath, appPublicPath));
  const expectedRoute = splitUrlPathSuffix(normalizeAppLocalUrlPath(appLocalPath, appPublicPath));
  return resolvedRoute.pathname === expectedRoute.pathname;
}

/**
 * Creates the narrow browser-path adapter needed by apps with hand-written
 * routing. API/service URLs must remain on their own endpoint contracts.
 */
export function createAppPublicPathHelpers(
  appPublicPath?: string | null,
): AppPublicPathHelpers {
  const normalizedPublicPath = appPublicPath === undefined
    ? readAppPublicPathFromEnv()
    : normalizeAppPublicPath(appPublicPath);
  const browserBaseName = normalizedPublicPath || "/";

  function toAppLocalPathname(browserPathname: string) {
    const localPath = toAppLocalUrlPathFromBrowser(browserPathname);
    return splitUrlPathSuffix(localPath).pathname;
  }

  function toAppLocalUrlPathFromBrowser(browserUrlPath: string) {
    return toAppLocalUrlPath(browserUrlPath, normalizedPublicPath);
  }

  function toBrowserPath(appLocalUrlPath: string) {
    return withAppPublicUrlPath(appLocalUrlPath, normalizedPublicPath);
  }

  function normalizeAppLocalUrlPathForApp(appLocalUrlPath: string) {
    return normalizeAppLocalUrlPath(appLocalUrlPath, normalizedPublicPath);
  }

  return {
    browserBaseName,
    isAppLocalPathname: (browserPathname, appLocalPathname) =>
      matchesAppLocalUrlPath(browserPathname, appLocalPathname, normalizedPublicPath),
    normalizeAppLocalUrlPath: normalizeAppLocalUrlPathForApp,
    toAppLocalPathname,
    toAppLocalUrlPath: toAppLocalUrlPathFromBrowser,
    toBrowserPath,
  };
}
